/**
 * Layout do mosaico de panes, por projeto.
 *
 * Mosaico e nao janelas flutuantes: terminal sobreposto e terminal ilegivel.
 * Aqui os panes dividem o espaco, nunca se escondem, e o usuario ajusta as
 * proporcoes arrastando as divisorias.
 *
 * Este modulo e logica pura -- nada de React nem de DOM -- para o calculo de
 * proporcao poder ser raciocinado e corrigido sem mexer na renderizacao.
 */

/**
 * Nome e cor que o usuario deu a um pane.
 *
 * Mora aqui, no layout do projeto, e nao no daemon: o daemon nao tem rota de
 * rename e a sessao dele e um processo, nao uma preferencia de apresentacao.
 * O custo dessa escolha e que apelido e cor valem por navegador -- nao seguem
 * o usuario do app desktop para o browser.
 */
export interface PanePrefs {
  /** Ausente ou vazio: usa o `title` que veio do daemon. */
  name?: string;
  /** Ausente: herda a cor do agente. */
  color?: string;
  /**
   * Tamanho fixo da fonte do terminal, em px. Ausente: vale a regra global
   * (auto-ajuste ou tamanho base). Serve para o pane que voce quer ler de
   * longe, ou o que aceita fonte miuda por ser so log.
   */
  fontSize?: number;
}

/** Mesmo teto do `title` no daemon (routes/sessions.ts), por consistencia. */
export const MAX_PANE_NAME = 120;

export interface WorkspaceLayout {
  /** Ids das sessoes na ordem em que aparecem. */
  order: string[];
  /** Ids recolhidos para a barra inferior. */
  minimized: string[];
  /** Colunas fixadas pelo usuario; 0 = automatico pela quantidade. */
  columns: number;
  /** Fracao de largura de cada coluna. Soma livre -- o CSS normaliza com fr. */
  colFractions: number[];
  /** Fracao de altura de cada linha. */
  rowFractions: number[];
  /** Nome e cor por pane, indexados pelo id da sessao. */
  panePrefs: Record<string, PanePrefs>;
}

export const MAX_COLUMNS = 4;
/** Espessura da divisoria arrastavel, em px. */
export const GUTTER = 6;
/** Fracao minima para um pane nao virar uma fatia inutilizavel. */
const MIN_FRACTION = 0.12;

const STORAGE_PREFIX = 'cm.layout.';

export function emptyLayout(): WorkspaceLayout {
  return {
    order: [],
    minimized: [],
    columns: 0,
    colFractions: [],
    rowFractions: [],
    panePrefs: {},
  };
}

export function loadLayout(workspaceId: string): WorkspaceLayout {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + workspaceId);
    if (!raw) return emptyLayout();
    const parsed = JSON.parse(raw) as Partial<WorkspaceLayout>;
    return {
      order: Array.isArray(parsed.order) ? parsed.order.filter((x) => typeof x === 'string') : [],
      minimized: Array.isArray(parsed.minimized)
        ? parsed.minimized.filter((x) => typeof x === 'string')
        : [],
      columns: typeof parsed.columns === 'number' ? clampColumns(parsed.columns) : 0,
      colFractions: numbers(parsed.colFractions),
      rowFractions: numbers(parsed.rowFractions),
      panePrefs: panePrefsOf(parsed.panePrefs),
    };
  } catch {
    // localStorage cheio, JSON corrompido, modo privado: layout e conveniencia,
    // nunca deve impedir o cockpit de abrir.
    return emptyLayout();
  }
}

export function saveLayout(workspaceId: string, layout: WorkspaceLayout): void {
  try {
    localStorage.setItem(STORAGE_PREFIX + workspaceId, JSON.stringify(layout));
  } catch {
    /* sem persistencia; o layout vale so para esta sessao */
  }
}

function numbers(value: unknown): number[] {
  return Array.isArray(value)
    ? value.filter((x): x is number => typeof x === 'number' && Number.isFinite(x) && x > 0)
    : [];
}

const HEX_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/**
 * Valida o registro de preferencias vindo do localStorage.
 *
 * Exportada -- e nao embutida no `loadLayout` -- porque e aqui que mora a
 * validacao, e o `loadLayout` toca `localStorage`, que nao existe no Node em
 * que os testes rodam. Separar deixa a parte que importa testavel.
 *
 * Entrada que nao produz nem nome nem cor e descartada em vez de virar `{}`:
 * um registro cheio de objetos vazios faria o `syncLayout` comparar
 * diferente e gravar sem motivo.
 */
export function panePrefsOf(value: unknown): Record<string, PanePrefs> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const out: Record<string, PanePrefs> = {};
  for (const [id, raw] of Object.entries(value as Record<string, unknown>)) {
    if (!id || !raw || typeof raw !== 'object' || Array.isArray(raw)) continue;
    const { name, color } = raw as { name?: unknown; color?: unknown };
    const pref: PanePrefs = {};
    if (typeof name === 'string' && name.trim()) {
      pref.name = name.trim().slice(0, MAX_PANE_NAME);
    }
    if (typeof color === 'string' && HEX_RE.test(color.trim())) {
      pref.color = color.trim().toLowerCase();
    }
    const { fontSize } = raw as { fontSize?: unknown };
    if (typeof fontSize === 'number' && Number.isFinite(fontSize)) {
      pref.fontSize = Math.min(22, Math.max(6, Math.round(fontSize * 2) / 2));
    }
    if (pref.name !== undefined || pref.color !== undefined || pref.fontSize !== undefined) {
      out[id] = pref;
    }
  }
  return out;
}

/** O nome a mostrar: o apelido do usuario, ou o titulo que o daemon deu. */
export function paneName(
  layout: WorkspaceLayout,
  sessionId: string,
  fallback: string,
): string {
  return layout.panePrefs?.[sessionId]?.name || fallback;
}

/** A cor propria do pane, ou `null` quando ele herda a do agente. */
export function paneColor(layout: WorkspaceLayout, sessionId: string): string | null {
  return layout.panePrefs?.[sessionId]?.color ?? null;
}

/** Fonte fixa deste pane, ou `undefined` quando vale a regra global. */
export function paneFontSize(layout: WorkspaceLayout, sessionId: string): number | undefined {
  return layout.panePrefs?.[sessionId]?.fontSize;
}

/**
 * Grava (ou limpa) as preferencias de um pane.
 *
 * Campo com valor vazio remove a chave, e entrada que ficou sem nada sai do
 * registro -- e assim que "renomear para vazio" volta ao titulo do daemon e
 * "herdar a cor do agente" apaga o override, sem precisar de um botao de
 * restaurar para cada coisa.
 */
export function setPanePrefs(
  layout: WorkspaceLayout,
  sessionId: string,
  patch: PanePrefs,
): WorkspaceLayout {
  const current = layout.panePrefs?.[sessionId] ?? {};
  const merged: PanePrefs = { ...current };

  if ('name' in patch) {
    const name = patch.name?.trim().slice(0, MAX_PANE_NAME);
    if (name) merged.name = name;
    else delete merged.name;
  }
  if ('color' in patch) {
    if (patch.color) merged.color = patch.color.toLowerCase();
    else delete merged.color;
  }
  if ('fontSize' in patch) {
    if (patch.fontSize) merged.fontSize = Math.min(22, Math.max(6, patch.fontSize));
    else delete merged.fontSize;
  }

  const panePrefs = { ...(layout.panePrefs ?? {}) };
  if (
    merged.name === undefined &&
    merged.color === undefined &&
    merged.fontSize === undefined
  ) delete panePrefs[sessionId];
  else panePrefs[sessionId] = merged;

  return { ...layout, panePrefs };
}

function clampColumns(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.min(MAX_COLUMNS, Math.max(0, Math.trunc(n)));
}

/**
 * Casa o layout guardado com as sessoes que existem agora.
 *
 * Sessoes aparecem e somem o tempo todo; sem essa reconciliacao o layout
 * guardaria ids mortos e as sessoes novas nao teriam lugar.
 */
export function reconcile(layout: WorkspaceLayout, sessionIds: string[]): WorkspaceLayout {
  const alive = new Set(sessionIds);
  const kept = layout.order.filter((id) => alive.has(id));
  const added = sessionIds.filter((id) => !kept.includes(id));
  const order = [...kept, ...added];
  const minimized = layout.minimized.filter((id) => alive.has(id));

  const visible = order.filter((id) => !minimized.includes(id)).length;
  const cols = effectiveColumns(layout.columns, visible);
  const rows = cols > 0 ? Math.ceil(visible / cols) : 0;

  return {
    order,
    minimized,
    columns: layout.columns,
    // Redimensionar a grade invalida as fracoes antigas: se o numero de
    // colunas mudou, as proporcoes anteriores nao descrevem mais esta grade.
    colFractions: fitFractions(layout.colFractions, cols),
    rowFractions: fitFractions(layout.rowFractions, rows),
    // Preferencia de pane morto e lixo: id de sessao nunca se repete, entao
    // sem esta poda o cm.layout.<id> cresceria para sempre.
    //
    // Filtrar as entradas existentes, em vez de reconstruir a partir de
    // `order`, preserva a ordem de insercao das chaves. O syncLayout compara
    // os layouts por JSON.stringify (store.ts): reordenar as chaves geraria
    // uma diferenca falsa e uma gravacao a cada sync.
    panePrefs: Object.fromEntries(
      Object.entries(layout.panePrefs ?? {}).filter(([id]) => alive.has(id)),
    ),
  };
}

/** Quantas colunas usar de fato: a escolha do usuario, ou uma automatica. */
export function effectiveColumns(chosen: number, visibleCount: number): number {
  if (visibleCount === 0) return 0;
  if (chosen > 0) return Math.min(chosen, visibleCount);
  // Automatico: 1 pane ocupa tudo, 2 a 4 usam 2 colunas, acima disso 3.
  // Mais colunas que isso e o terminal fica estreito demais para ser util.
  // Com 3 panes as 2 colunas nao formam um retangulo cheio: o encaixe fica
  // a cargo de planMosaic, que entrega a sobra para um pane em vez de
  // deixar um buraco.
  if (visibleCount === 1) return 1;
  if (visibleCount <= 4) return 2;
  return 3;
}

/**
 * Ajusta a lista de fracoes para ter exatamente `count` itens, preservando as
 * proporcoes que o usuario ja tinha definido.
 */
function fitFractions(current: number[], count: number): number[] {
  if (count <= 0) return [];
  if (current.length === count) return current;
  if (current.length > count) return current.slice(0, count);
  return [...current, ...Array(count - current.length).fill(1)];
}

/** Posicao de um pane na grade, em coordenadas de linha/coluna (base 0). */
export function cellOf(index: number, columns: number): { col: number; row: number } {
  return { col: index % columns, row: Math.floor(index / columns) };
}

/** Onde um pane senta, e quantas faixas ele cobre. */
export interface MosaicPane {
  index: number;
  col: number;
  row: number;
  colSpan: number;
  rowSpan: number;
}

/**
 * Um pedaco de divisoria. So existe onde dois panes realmente se encostam,
 * para o traco nao atravessar um pane que ocupa a sobra.
 */
export interface MosaicGutter {
  axis: 'col' | 'row';
  /** Indice da faixa da esquerda (ou de cima) — o mesmo de resizeFractions. */
  index: number;
  col: number;
  row: number;
  colSpan: number;
  rowSpan: number;
}

export interface MosaicPlan {
  cols: number;
  rows: number;
  panes: MosaicPane[];
  gutters: MosaicGutter[];
}

/**
 * Encaixa `count` panes em `columns` colunas sem celula vazia.
 *
 * Quando a conta fecha (1, 2, 4, 6, 9...), cada pane ocupa uma celula.
 * Quando sobra espaco, um pane absorve o resto:
 *
 * - se a ultima coluna ficaria com um unico pane e o restante vazio
 *   (3 panes em 2 colunas, 5 em 3, 7 em 3), esse pane fica na lateral,
 *   de alto a baixo, e os outros empilham nas colunas da esquerda;
 * - nos outros casos o ultimo pane alarga e cobre as celulas vazias da
 *   ultima linha.
 *
 * O ultimo da ordem e quem fica com a sobra. Arrastar um pane para o fim
 * da ordem entrega esse lugar a ele.
 */
export function planMosaic(count: number, columns: number): MosaicPlan {
  if (count <= 0 || columns <= 0) return { cols: 0, rows: 0, panes: [], gutters: [] };

  const cols = Math.min(columns, count);
  const rows = Math.ceil(count / cols);
  const panes: MosaicPane[] = [];
  // A coluna da direita tem exatamente um pane, e as outras estao cheias.
  const tallRemainder = cols > 1 && (cols - 1) * rows === count - 1;

  if (tallRemainder) {
    const innerCols = cols - 1;
    for (let i = 0; i < count - 1; i++) {
      panes.push({
        index: i,
        col: i % innerCols,
        row: Math.floor(i / innerCols),
        colSpan: 1,
        rowSpan: 1,
      });
    }
    panes.push({ index: count - 1, col: cols - 1, row: 0, colSpan: 1, rowSpan: rows });
  } else {
    const remainder = rows * cols - count;
    for (let i = 0; i < count; i++) {
      const isLast = i === count - 1;
      panes.push({
        index: i,
        col: i % cols,
        row: Math.floor(i / cols),
        colSpan: isLast ? 1 + remainder : 1,
        rowSpan: 1,
      });
    }
  }

  return { cols, rows, panes, gutters: guttersFor(panes, cols, rows) };
}

function guttersFor(panes: MosaicPane[], cols: number, rows: number): MosaicGutter[] {
  const owner: number[][] = Array.from({ length: rows }, () => Array(cols).fill(-1));
  for (const pane of panes) {
    for (let row = pane.row; row < pane.row + pane.rowSpan; row++) {
      for (let col = pane.col; col < pane.col + pane.colSpan; col++) {
        owner[row][col] = pane.index;
      }
    }
  }

  const gutters: MosaicGutter[] = [];

  for (let col = 0; col < cols - 1; col++) {
    let start: number | null = null;
    for (let row = 0; row <= rows; row++) {
      const split =
        row < rows && owner[row][col] !== -1 && owner[row][col] !== owner[row][col + 1];
      if (split && start === null) start = row;
      if (!split && start !== null) {
        gutters.push({
          axis: 'col',
          index: col,
          col,
          row: start,
          colSpan: 1,
          rowSpan: row - start,
        });
        start = null;
      }
    }
  }

  for (let row = 0; row < rows - 1; row++) {
    let start: number | null = null;
    for (let col = 0; col <= cols; col++) {
      const split =
        col < cols && owner[row][col] !== -1 && owner[row][col] !== owner[row + 1][col];
      if (split && start === null) start = col;
      if (!split && start !== null) {
        gutters.push({
          axis: 'row',
          index: row,
          col: start,
          row,
          colSpan: col - start,
          rowSpan: 1,
        });
        start = null;
      }
    }
  }

  return gutters;
}

/** `grid-column` / `grid-row` de um pane, pulando as faixas de divisoria. */
export function gridPlacement(
  col: number,
  row: number,
  colSpan: number,
  rowSpan: number,
): { gridColumn: string; gridRow: string } {
  return {
    gridColumn: trackSpan(trackLine(col), colSpan),
    gridRow: trackSpan(trackLine(row), rowSpan),
  };
}

/** Posicao da divisoria na grade. O eixo do arraste senta na faixa de 6px. */
export function gutterPlacement(gutter: MosaicGutter): { gridColumn: string; gridRow: string } {
  if (gutter.axis === 'col') {
    return {
      gridColumn: String(gutterLine(gutter.index)),
      gridRow: trackSpan(trackLine(gutter.row), gutter.rowSpan),
    };
  }
  return {
    gridRow: String(gutterLine(gutter.index)),
    gridColumn: trackSpan(trackLine(gutter.col), gutter.colSpan),
  };
}

/**
 * `span` faixas de conteudo. Entre elas existem `span - 1` divisorias, entao
 * a area cobre `span * 2 - 1` trilhas. Span 1 fica so no numero da trilha,
 * que e o que o CSS Grid espera para uma unica celula.
 */
function trackSpan(start: number, span: number): string {
  if (span <= 1) return String(start);
  return `${start} / span ${span * 2 - 1}`;
}

/**
 * Aplica o arraste de uma divisoria.
 *
 * `delta` e a fracao do espaco total que saiu de um lado e entrou no outro.
 * As duas faixas vizinhas trocam entre si, entao o total nao muda e nenhuma
 * outra faixa se move -- o comportamento que se espera de um separador.
 */
export function resizeFractions(
  fractions: number[],
  gutterIndex: number,
  delta: number,
): number[] {
  const a = fractions[gutterIndex];
  const b = fractions[gutterIndex + 1];
  if (a === undefined || b === undefined) return fractions;

  const total = fractions.reduce((sum, f) => sum + f, 0);
  const pair = a + b;
  const moved = delta * total;

  // O minimo e por faixa e proporcional ao total, para nao deixar um pane
  // virar uma tira de 3 pixels da qual nao se consegue mais sair.
  const min = MIN_FRACTION * total;
  const nextA = Math.min(pair - min, Math.max(min, a + moved));
  const nextB = pair - nextA;

  const out = [...fractions];
  out[gutterIndex] = nextA;
  out[gutterIndex + 1] = nextB;
  return out;
}

/** Move um pane de uma posicao para outra, empurrando os demais. */
export function moveInOrder(order: string[], fromId: string, toId: string): string[] {
  const from = order.indexOf(fromId);
  const to = order.indexOf(toId);
  if (from === -1 || to === -1 || from === to) return order;
  const out = [...order];
  const [moved] = out.splice(from, 1);
  out.splice(to, 0, moved);
  return out;
}

/** `grid-template-*` com as divisorias intercaladas entre as faixas. */
export function templateWithGutters(fractions: number[]): string {
  if (fractions.length === 0) return '';
  return fractions.map((f) => `${f}fr`).join(` ${GUTTER}px `);
}

/** Indice de grid (base 1) de uma faixa, contando as divisorias. */
export function trackLine(index: number): number {
  return index * 2 + 1;
}

/** Indice de grid (base 1) de uma divisoria. */
export function gutterLine(index: number): number {
  return index * 2 + 2;
}
